import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Loading, PageHead, useAdmin } from "./ui";

const AUTO = [
  { key: "reminder", label: "Appointment reminder (day before, 9 AM)", fields: [["reminder_enabled", "Send this email", "bool"], ["reminder_subject", "Subject"], ["reminder_message", "Message", "text"]] },
  { key: "birthday", label: "Birthday Club email (on their birthday, 9 AM)", fields: [["birthday_enabled", "Send this email", "bool"], ["birthday_subject", "Subject"], ["birthday_message", "Message", "text"], ["birthday_offer", "Highlighted offer"]] },
  { key: "thankyou", label: "Thank-you after a visit (day after, 9 AM)", fields: [["thankyou_enabled", "Send this email", "bool"], ["thankyou_subject", "Subject"], ["thankyou_message", "Message", "text"], ["thankyou_button", "Rebook button text"], ["review_label", "Google review link text"], ["review_url", "Google review link (https://…) — empty hides it"]] },
];

const Preview = ({ k, draft }) => {
  const [p, setP] = useState(null);
  const timer = useRef();
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { const { data } = await api.post("/admin/emails/preview", { key: k, emails: draft.emails, automations: draft.automations }); setP(data); } catch { setP(null); }
    }, 500);
    return () => clearTimeout(timer.current);
  }, [k, draft]);
  return (
    <div className="em-preview" data-testid={`email-preview-${k}`}>
      <p className="em-preview-subject"><span>Subject:</span> <strong data-testid={`email-preview-${k}-subject`}>{p?.subject ?? "…"}</strong></p>
      {p ? <iframe title={`Preview of ${k}`} className="em-preview-frame" sandbox="" srcDoc={p.html} /> : <div className="em-preview-frame em-preview-empty"><Loader2 size={16} className="spin" /></div>}
    </div>
  );
};

const TestButton = ({ k, email }) => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/admin/emails/test", { key: k }); toast.success(`Test sent to ${email}`); } catch (err) { toast.error(formatApiError(err), { duration: 8000 }); } finally { setBusy(false); }
  };
  return <button type="button" className="btn light em-test" disabled={busy} onClick={send} data-testid={`email-test-${k}`}>{busy ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Send me a test</button>;
};

const Template = ({ m, t, onChange, email, draft }) => (
  <div className="ad-card em-card" data-testid={`email-template-${m.key}`}>
    <h3>{m.label}</h3>
    <div className="em-split">
    <div className="ad-fields">
      <div className="field"><label>Subject</label><input maxLength={160} value={t.subject} onChange={(e) => onChange({ ...t, subject: e.target.value })} data-testid={`email-${m.key}-subject`} /></div>
      <div className="field"><label>Message</label>
        <p className="ad-hint">One paragraph per line. Wrap words in **double stars** for bold. Booking/order details are added automatically below your message.</p>
        <textarea rows={5} maxLength={4000} value={t.message} onChange={(e) => onChange({ ...t, message: e.target.value })} data-testid={`email-${m.key}-message`} />
      </div>
      <div className="em-ph" data-testid={`email-${m.key}-placeholders`}>{m.placeholders.map((p) => <code key={p}>{`{${p}}`}</code>)}</div>
      <TestButton k={m.key} email={email} />
    </div>
    <Preview k={m.key} draft={draft} />
    </div>
  </div>
);

const AutoCard = ({ a, data, onChange, email, draft }) => (
  <div className="ad-card em-card" data-testid={`email-template-${a.key}`}>
    <h3>{a.label}</h3>
    <div className="em-split">
    <div className="ad-fields">
      {a.fields.map(([k, label, kind]) => (
        <div className="field" key={k}>
          <label>{label}</label>
          {kind === "bool" ? <label className="ad-check"><input type="checkbox" checked={!!data[k]} onChange={(e) => onChange({ ...data, [k]: e.target.checked })} data-testid={`email-${k}`} /> {data[k] ? "On" : "Off"}</label>
            : kind === "text" ? <textarea rows={4} value={data[k]} onChange={(e) => onChange({ ...data, [k]: e.target.value })} data-testid={`email-${k}`} />
            : <input value={data[k]} onChange={(e) => onChange({ ...data, [k]: e.target.value })} data-testid={`email-${k}`} />}
        </div>
      ))}
      <TestButton k={a.key} email={email} />
    </div>
    <Preview k={a.key} draft={draft} />
    </div>
  </div>
);

export default function Emails({ me }) {
  const { data } = useAdmin("emails", "/admin/emails");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  useEffect(() => { if (data && !draft) setDraft({ emails: structuredClone(data.emails), automations: structuredClone(data.automations) }); }, [data, draft]);
  const save = async () => {
    setBusy(true);
    try {
      const { data: saved } = await api.put("/admin/emails", draft);
      qc.setQueryData(["admin", "emails"], saved);
      setDraft({ emails: structuredClone(saved.emails), automations: structuredClone(saved.automations) });
      qc.invalidateQueries({ queryKey: ["content"] });
      qc.invalidateQueries({ queryKey: ["admin", "content-history"] });
      toast.success("Email wording saved");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  if (!data || !draft) return <Loading />;
  const groups = [...new Set(data.meta.map((m) => m.group))];
  const saveBtn = (tid) => <button className="btn gold" onClick={save} disabled={busy} data-testid={tid}>{busy ? <Loader2 size={16} className="spin" /> : "Save changes"}</button>;
  return (
    <div data-testid="admin-emails">
      <PageHead eyebrow="Wording of every email clients receive" title="Emails">{saveBtn("emails-save-button")}</PageHead>
      <p className="muted-sm">Edit the subject and message of each email. Placeholders like <code>{"{first_name}"}</code> are filled in automatically. The preview beside each email updates as you type. “Send me a test” emails the current wording to <strong>{me.email}</strong> with sample details.</p>
      {groups.map((g) => (
        <section key={g} data-testid={`email-group-${g.toLowerCase().replace(/ /g, "-")}`}>
          <h2 className="em-group">{g}</h2>
          {data.meta.filter((m) => m.group === g).map((m) => (
            <Template key={m.key} m={m} t={draft.emails[m.key]} email={me.email} draft={draft} onChange={(t) => setDraft({ ...draft, emails: { ...draft.emails, [m.key]: t } })} />
          ))}
        </section>
      ))}
      <section data-testid="email-group-automatic-emails">
        <h2 className="em-group">Automatic emails</h2>
        {AUTO.map((a) => <AutoCard key={a.key} a={a} data={draft.automations} email={me.email} draft={draft} onChange={(automations) => setDraft({ ...draft, automations })} />)}
      </section>
      <div className="ad-savebar">{saveBtn("emails-save-bottom-button")}</div>
    </div>
  );
}
